import React, { useState, useEffect, useCallback } from 'react';
import { MagnifyingGlassIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import OpportunityCard from './OpportunityCard';

/**
 * OpportunityGrid component for displaying startup opportunities in a responsive grid layout
 * Features loading states, empty state handling, and pagination/infinite scroll
 */
const OpportunityGrid = ({ 
  opportunities = [], 
  loading = false, 
  error = null,
  onConnect,
  onClick,
  onLoadMore,
  onRetry,
  hasMore = false,
  className = ''
}) => {
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Handle infinite scroll
  const handleScroll = useCallback(() => {
    if (loading || isLoadingMore || !hasMore || !onLoadMore) return;

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;

    // Trigger load more when user is 200px from bottom
    if (scrollTop + windowHeight >= documentHeight - 200) {
      setIsLoadingMore(true);
      onLoadMore().finally(() => setIsLoadingMore(false));
    }
  }, [loading, isLoadingMore, hasMore, onLoadMore]);

  // Set up scroll listener for infinite scroll
  useEffect(() => {
    if (hasMore && onLoadMore) {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll, hasMore, onLoadMore]);

  // Loading skeleton component
  const LoadingSkeleton = () => (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 h-full animate-pulse">
      {/* Header skeleton */}
      <div className="flex items-start justify-between mb-4">
        <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
        <div className="w-20 h-8 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
      </div>
      
      {/* Company name skeleton */}
      <div className="w-3/4 h-6 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
      
      {/* Description skeleton */}
      <div className="space-y-2 mb-4">
        <div className="w-full h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
        <div className="w-5/6 h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
        <div className="w-2/3 h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
      </div>
      
      {/* Funding info skeleton */}
      <div className="w-1/2 h-4 bg-gray-200 dark:bg-gray-700 rounded mb-3"></div>
      
      {/* Role info skeleton */}
      <div className="space-y-2 mb-4">
        <div className="w-2/3 h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
        <div className="w-1/2 h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
      </div>
      
      {/* Highlights skeleton */}
      <div className="space-y-2 mb-4">
        <div className="w-1/3 h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
        <div className="w-4/5 h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
        <div className="w-3/4 h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
      </div>
      
      {/* Badge skeleton */}
      <div className="w-1/2 h-6 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
    </div>
  );

  // Error state component
  const ErrorState = () => (
    <div className="col-span-full flex flex-col items-center justify-center py-12 px-4">
      <div className="text-center max-w-md">
        <ExclamationTriangleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Unable to Load Opportunities
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          {error || 'There was an error loading startup opportunities. Please try again.'}
        </p>
        <button
          onClick={onRetry || (() => window.location.reload())}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium 
                   rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 
                   focus:ring-purple-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
        >
          Try Again
        </button>
      </div>
    </div>
  );

  // Empty state component
  const EmptyState = () => (
    <div className="col-span-full flex flex-col items-center justify-center py-12 px-4">
      <div className="text-center max-w-md">
        <MagnifyingGlassIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          No Opportunities Found
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          We couldn't find any startup opportunities matching your criteria. Try adjusting your filters or check back later for new opportunities.
        </p>
        <div className="space-y-2 text-sm text-gray-500 dark:text-gray-400">
          <p>• New opportunities are added regularly</p>
          <p>• Try broadening your search criteria</p>
          <p>• Check back tomorrow for fresh listings</p>
        </div>
      </div>
    </div>
  );

  // Loading more indicator
  const LoadingMoreIndicator = () => (
    <div className="col-span-full flex justify-center py-8">
      <div className="flex items-center gap-3 text-gray-600 dark:text-gray-400">
        <div className="w-5 h-5 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
        <span className="text-sm font-medium">Loading more opportunities...</span>
      </div>
    </div>
  );



  // Main render logic
  if (error) {
    return (
      <div className={`grid grid-cols-1 ${className}`}>
        <ErrorState />
      </div>
    );
  }

  if (loading && opportunities.length === 0) {
    return (
      <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 ${className}`}>
        {/* Show 6 loading skeletons */}
        {Array.from({ length: 6 }).map((_, index) => (
          <LoadingSkeleton key={index} />
        ))}
      </div>
    );
  }

  if (!loading && opportunities.length === 0) {
    return (
      <div className={`grid grid-cols-1 ${className}`}>
        <EmptyState />
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Opportunities Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {opportunities.map((opportunity) => (
          <OpportunityCard
            key={opportunity.id}
            opportunity={opportunity}
            onConnect={onConnect}
            onClick={onClick}
          />
        ))}
      </div>

      {/* Loading More Indicator */}
      {isLoadingMore && (
        <div className="mt-8">
          <LoadingMoreIndicator />
        </div>
      )}

      {/* Load More Button (alternative to infinite scroll) */}
      {!isLoadingMore && hasMore && onLoadMore && (
        <div className="flex justify-center mt-8">
          <button
            onClick={() => {
              setIsLoadingMore(true);
              onLoadMore().finally(() => setIsLoadingMore(false));
            }}
            className="px-6 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 
                     text-gray-700 dark:text-gray-300 font-medium rounded-lg
                     hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500
                     focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 
                     dark:focus:ring-offset-gray-900 transition-all duration-200
                     shadow-sm hover:shadow-md"
          >
            Load More Opportunities
          </button>
        </div>
      )}

      {/* End of Results Indicator */}
      {!hasMore && opportunities.length > 0 && (
        <div className="flex justify-center mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
          <div className="text-center text-gray-500 dark:text-gray-400">
            <p className="text-sm font-medium mb-1">You've seen all available opportunities</p>
            <p className="text-xs">Check back later for new listings</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default OpportunityGrid;